import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setSnackBar } from "../../../../stores/ui/ui-action";
import { updateDisconnectMgmtStatus } from "../../../../services/bff-service";
import { useStyles } from "./styles";
import { Box } from "@material-ui/core";
import moment from "moment";
import CalendarPicker from '../../../../components/portico/calendar-picker/calendar-picker'

interface RenewalCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  company: any;
  fetchManagedCompaniesDetails: () => Promise<void>;
  setCircularProgressLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const RenewalCompanyDialog: React.FC<RenewalCompanyDialogProps> = ({
  open,
  onClose,
  company,
  fetchManagedCompaniesDetails,
  setCircularProgressLoading,
}) => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(null); // default tomorrow
  const { email } = useSelector((state: any) => state.user);
  const classes = useStyles();

  const handleConfirm = async () => {
    if (!selectedDate) return;
    onClose();
    setCircularProgressLoading(true);
    try {
      await updateDisconnectMgmtStatus({
        companyId: company.companyId,
        status: "pending_renewal",
        user_email: email,
        renewal_date: selectedDate,
        company_id: company.companyId,
        codat_company_id: company.codatCompanyId,
        ...(company.cif && { cif: company.cif }),
        ...(company.crmEntityId && { crm_entity_id: company.crmEntityId }),
        legal_entity_name: company.name,
        action_flags: company.action_flags,
        codat_connection: company.codatConnectionStatus,
        connection_id: company.connection_ids,
        team_code: company.teamCode,
        ...(company.ct_client_status && { ct_client_status: company.ct_client_status }),
        ...(company.ct_team_code && { ct_team_code: company.ct_team_code }),
        ...(company.cp_total_packages && { cp_total_packages: parseInt(company.cp_total_packages) }),
        ...(company.cp_completed_packages && { cp_completed_packages: parseInt(company.cp_completed_packages) }),
        ...(company.cp_team_code && { cp_team_code: company.cp_team_code }),
      });

      dispatch(
        setSnackBar({
          show: true,
          message: `Company set to “Pending Renewal”.`,
          messageType: "success",
        })
      );

      await fetchManagedCompaniesDetails();
    } catch (error) {
      console.log(error);
      dispatch(
        setSnackBar({
          show: true,
          message: `Failed to update client status (${error.message}). Please try again.`,
          messageType: "error",
        })
      );
    } finally {
      setCircularProgressLoading(false);
      setSelectedDate(null)
    }
  };

  const handleOnEndDateChange = (date) => {
    if (date) {
      setSelectedDate(moment(date).endOf("month").format("YYYY-MM-DD"));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle>Update Client Status to Renewal</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" classes={classes.dialogText}>
          To update {company?.name}’s status to “
          <Typography component="span" variant="body2" fontWeight="500" fontSize="14px">
            Pending Renewal
          </Typography>
          ”, please specify the expected renewal date:{" "}
        </Typography>

        <Box className={classes.calenderContainer}>
          <CalendarPicker
            name="fyEndDate"
            label="FY End Date"
            required={true}
            inputFormat={"MM/yyyy"}
            views={["year", "month"]}
            setValue={() => {}}
            value={""}
            minDate={new Date()}
            onChange={handleOnEndDateChange}
          />

          <Typography variant="caption" color="textSecondary" paddingX="14px">
            If the expected renewal date passes, and there's still a mismatch between the Client Status in Compliance
            Tracker vs LendX, then the company will be flagged again.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button onClick={handleConfirm} color="primary" disabled={!selectedDate}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewalCompanyDialog;
